import { trainingAPI } from "@api/TrainingAPI"
import type { Dayjs } from "dayjs"
import { Input, Row, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState, useMemo } from "react"
import { useSelector } from "react-redux"
import { audioMessage as message } from "@components/Common/Notifications/audioMessage"
import "../../../Styles/styles-misc.css";
import "./styles.css"
import { PlayerLevelBaslineMetrics } from './baseline_util.js'

const { Text } = Typography;

export const SecondStep = ({
    startDate,
    endDate,
    days,
    players,
    playerLabels,
    baselines,
    setBaselines
}: ISecondStep) => {
    const { metrics } = useSelector(({ team }) => team)
    const [data, setData] = useState<DataType[]>([])
    const [columnData, setColumnData] = useState<ColumnData[]>([])
    const [loading, setLoading] = useState(true)

    const allMetrics = useMemo(
        () => ([...(metrics || []), ...PlayerLevelBaslineMetrics]),
        [metrics]
    );

    useEffect(() => {
        (async () => {
            try {
                const result: IBaseline = await trainingAPI.baselineBuilder(startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"), days, players)

                const baselineData = result.data

                const metricKeyToMetricMapping: { [key: string]: string } = {}
                allMetrics.forEach((metric: any) => {
                    metricKeyToMetricMapping[metric.key] = metric.name
                })

                setData(Object.entries(baselineData).map((e) => {
                    const [key, val] = e

                    return {
                        name: metricKeyToMetricMapping[key],
                        key: key,
                        ...val
                    }
                }))

                setColumnData(result.columns)

                if (Object.values(baselines).length === 0) {
                    const emptyBaseline: { [key: string]: string | number } = {}
                    Object.keys(baselineData).forEach((metric: any) => {
                        emptyBaseline[metric] = ""
                    })
                    setBaselines(emptyBaseline)
                }
            } catch (e) {
                message.error("Couldn't load baseline recommendations. Please try again.")
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const handleInputChange = (key: string, value: string) => {
        // @ts-ignore
        setBaselines((prev: any) => ({ ...prev, [key]: value }));
    };

    const tableColumns: ColumnsType<DataType> = [
        {
            title: 'Metric Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 200,
            render: (text) => (
                <div style={{ textAlign: 'left' }}>
                    <Text ellipsis={true && { tooltip: text }}>{text}</Text>
                </div>
            ),
        },
        ...columnData.map((column) => {
            return {
                title: (
                    <Tooltip title={column.hover && column.hover}>
                        {column.name}
                    </Tooltip>
                ),
                dataIndex: column.key,
                key: column.key,
                width: 100,
                render: (text: any) => (
                        <Text>{text || 0}</Text>
                ),
                onCell: (record: DataType) => {
                    const cellValue = (record as any)[column.key];
                    return {
                        onClick: (event: any) => {
                            setBaselines({ ...baselines, [record.key]: (cellValue || 0).toString() });
                        },
                        style: {
                            backgroundColor: (Number(cellValue) === Number(baselines[record.key]) && baselines[record.key] !== "") ? '#FFFFC5' : undefined,
                        },
                        className: 'hover-link-style',
                    };
                },
            };
        }),
        {
            title: 'Baseline',
            dataIndex: 'baseline',
            key: 'baseline',
            width: 140,
            fixed: 'right',
            render: (_: any, record: DataType) => (
                <Input
                    value={baselines[record.key] || ''}
                    onChange={(e) => handleInputChange(record.key, e.target.value)}
                    type="number"
                    placeholder="Input Value"
                    style={{ height: '20px' }}
                />
            ),
        },
    ]

    return (
        <div style={{ marginBottom: '20px' }}>
            <Row style={{ marginBottom: '10px', rowGap: '4px' }}>
                <Text strong>Filters Applied:</Text>

                <Tag color="default" style={{ marginLeft: '2px', marginRight: '4px' }}>
                    {startDate.format("YYYY-MM-DD")} - {endDate.format("YYYY-MM-DD")}
                </Tag>

                {days.length !== 0 &&
                    <Tag color="default" style={{ marginRight: '4px' }}>
                        Include: {days.join(", ")}
                    </Tag>
                }

                {playerLabels.length !== 0 &&
                    <Tag color="default">
                        Players: {playerLabels.join(", ")}
                    </Tag>
                }
            </Row>

            {loading ? (
                <Spin spinning>
                    <div style={{ minHeight: 280 }} />
                </Spin>
            ) : (
                <Table className="compact-table" columns={tableColumns} dataSource={data} pagination={false} scroll={{ y: 280 }} size="small" />
            )}
        </div>
    )
}

interface ISecondStep {
    startDate: Dayjs
    endDate: Dayjs
    days: string[]
    players: string[],
    playerLabels: string[],
    baselines: { [key: string]: number | string }
    setBaselines: React.Dispatch<{ [key: string]: number | string }>
}

type DataType = ({
    name: string,
    key: string,
} & BaselineMetrics)

type BaselineMetrics = {
    min: number
    max: number
    avg: number
    variance: number
    std_dev: number
    std_dev_minus_2: number
    std_dev_minus_1: number
    std_dev_plus_2: number
    std_dev_plus_1: number
};

type ColumnData = {
    key: string;
    name: string;
    hover?: string;
}

interface IBaseline {
    data: {
        [key: string]: BaselineMetrics
    },
    columns: ColumnData[]
}

export default SecondStep